import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Container,
  Dropdown,
  Form,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
  Segment,
} from "semantic-ui-react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

import {
  getApp,
  getApps,
  initializeApp,
} from "firebase/app";

import {
  auth,
  db,
  firebaseConfig,
} from "../firebase/firebaseConfig";

import EstadoTenant from "./EstadoTenant";

import Swal from "sweetalert2";

// ============================================================
// FIREBASE AUTH SECUNDARIO
// ============================================================
//
// IMPORTANTE:
// El usuario DADMIN/JADMIN permanece conectado en la aplicación
// principal.
//
// La creación del nuevo ADMIN-HC ocurre en una instancia secundaria
// de Firebase Authentication.
//
// Así evitamos que createUserWithEmailAndPassword reemplace la
// sesión actual de BackOffice.
// ============================================================

const secondaryApp =
  getApps().some((app) => app.name === "Secondary")
    ? getApp("Secondary")
    : initializeApp(firebaseConfig, "Secondary");

const secondaryAuth = getAuth(secondaryApp);

// ============================================================
// HELPERS
// ============================================================

const normalizarTexto = (valor) =>
  String(valor || "").trim().toUpperCase();

const normalizarCorreo = (valor) =>
  String(valor || "").trim().toLowerCase();

const obtenerMaxUsuarios = (empresa) => {
  return (
    Number(
      empresa?.maxUsers ||
      empresa?.maxusuarios ||
      empresa?.max_usuarios ||
      1
    ) || 1
  );
};

// ============================================================
// GENERAR PASSWORD TEMPORAL
// ============================================================
//
// El administrador de BackOffice NO necesita conocer esta clave.
//
// Firebase necesita una contraseña para crear la cuenta mediante
// createUserWithEmailAndPassword.
//
// Después enviamos sendPasswordResetEmail() para que el usuario
// defina su contraseña definitiva.
// ============================================================

const generarPasswordTemporal = () => {
  let aleatorio = "";

  try {
    const valores = new Uint32Array(4);
    window.crypto.getRandomValues(valores);

    aleatorio = Array.from(valores)
      .map((n) => n.toString(36))
      .join("");
  } catch (error) {
    aleatorio =
      Math.random().toString(36).slice(2) +
      Date.now().toString(36);
  }

  return `Lm!${aleatorio.slice(0, 18)}9aA`;
};

const EMPID_ST_INTERNO = String(
  process.env.REACT_APP_EMPID_STINTERNO || ""
).trim();

// ============================================================
// COMPONENTE
// ============================================================

const EstadoTenantPage = () => {
  // ==========================================================
  // EMPRESAS
  // ==========================================================

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");

  const [loadingEmpresas, setLoadingEmpresas] =
    useState(true);

  // ==========================================================
  // USUARIOS TENANT
  // ==========================================================

  const [usuariosTenant, setUsuariosTenant] =
    useState([]);

  const [loadingUsuarios, setLoadingUsuarios] =
    useState(false);

  // ==========================================================
  // MODAL ADMIN
  // ==========================================================

  const [modalAdminOpen, setModalAdminOpen] =
    useState(false);

  const [nombreAdmin, setNombreAdmin] =
    useState("");

  const [apellidoAdmin, setApellidoAdmin] =
    useState("");

  const [correoAdmin, setCorreoAdmin] =
    useState("");

  const [creandoAdmin, setCreandoAdmin] =
    useState(false);

  // ==========================================================
  // MENSAJES
  // ==========================================================

  const [mensaje, setMensaje] =
    useState(null);

  // ==========================================================
  // CARGAR EMPRESAS
  // ==========================================================
  useEffect(() => {
    const cargarEmpresas = async () => {
      try {
        setLoadingEmpresas(true);
        setMensaje(null);

        if (!EMPID_ST_INTERNO) {
          console.error(
            "Falta configurar REACT_APP_EMPID_STINTERNO."
          );

          setEmpresas([]);
          setEmpresaId("");

          setMensaje({
            tipo: "error",
            texto:
              "No fue posible cargar los tenants porque falta la configuración de la empresa interna ST.",
          });

          return;
        }

        const snap = await getDocs(
          collection(db, "empresas")
        );

        const data = snap.docs
          .map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
          }))
          .filter(
            (empresa) =>
              String(empresa.id).trim() !== EMPID_ST_INTERNO
          );

        data.sort((a, b) =>
          String(a.empresa || a.nombre || "").localeCompare(
            String(b.empresa || b.nombre || ""),
            "es",
            {
              sensitivity: "base",
            }
          )
        );

        setEmpresas(data);

        if (data.length > 0) {
          setEmpresaId((actual) => {
            if (
              actual &&
              data.some((item) => item.id === actual)
            ) {
              return actual;
            }

            return data[0].id;
          });
        } else {
          setEmpresaId("");
        }
      } catch (error) {
        console.error(
          "Error al cargar empresas:",
          error
        );

        setEmpresas([]);
        setEmpresaId("");

        setMensaje({
          tipo: "error",
          texto:
            "No fue posible cargar las empresas registradas.",
        });
      } finally {
        setLoadingEmpresas(false);
      }
    };

    cargarEmpresas();
  }, []);


  // ==========================================================
  // ESCUCHAR USUARIOS DEL TENANT
  // ==========================================================

  useEffect(() => {
    if (!empresaId) {
      setUsuariosTenant([]);
      setLoadingUsuarios(false);
      return undefined;
    }

    setLoadingUsuarios(true);

    const qUsuarios = query(
      collection(db, "usuarios"),
      where("emp_id", "==", empresaId)
    );

    const unsubscribe = onSnapshot(
      qUsuarios,

      (snapshot) => {
        const data =
          snapshot.docs.map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
          }));

        setUsuariosTenant(data);
        setLoadingUsuarios(false);
      },

      (error) => {
        console.error(
          "Error al cargar usuarios del tenant:",
          error
        );

        setUsuariosTenant([]);
        setLoadingUsuarios(false);

        setMensaje({
          tipo: "error",
          texto:
            "No fue posible cargar los usuarios del tenant seleccionado.",
        });
      }
    );

    return () => unsubscribe();
  }, [empresaId]);

  // ==========================================================
  // EMPRESA SELECCIONADA
  // ==========================================================

  const empresaSeleccionada = useMemo(() => {
    return (
      empresas.find(
        (item) =>
          item.id === empresaId
      ) || null
    );
  }, [empresas, empresaId]);

  // ==========================================================
  // USUARIOS ACTIVOS
  // ==========================================================

  const usuariosActivos = useMemo(() => {
    return usuariosTenant.filter(
      (usuario) =>
        usuario.activo !== false
    ).length;
  }, [usuariosTenant]);

  // ==========================================================
  // ADMIN HOMECARE
  // ==========================================================

  const adminsHomeCare = useMemo(() => {
    return usuariosTenant
      .filter(
        (usuario) =>
          String(
            usuario.rol || ""
          )
            .trim()
            .toUpperCase() ===
          "ADMIN-HC"
      )
      .sort((a, b) => {
        const activoA =
          a.activo !== false
            ? 1
            : 0;

        const activoB =
          b.activo !== false
            ? 1
            : 0;

        return activoB - activoA;
      });
  }, [usuariosTenant]);

  const adminHomeCare =
    adminsHomeCare.length > 0
      ? adminsHomeCare[0]
      : null;

  // ==========================================================
  // OPCIONES EMPRESAS
  // ==========================================================

  const opcionesEmpresas = useMemo(() => {
    return empresas.map((empresa) => ({
      key: empresa.id,
      value: empresa.id,

      text:
        empresa.empresa ||
        empresa.nombre ||
        "Sin nombre",
    }));
  }, [empresas]);

  // ==========================================================
  // CAMBIAR EMPRESA
  // ==========================================================

  const cambiarEmpresa = (value) => {
    setEmpresaId(value || "");
    setMensaje(null);

    cerrarModalAdmin();
  };

  // ==========================================================
  // RESET MODAL
  // ==========================================================

  const limpiarFormularioAdmin = () => {
    setNombreAdmin("");
    setApellidoAdmin("");
    setCorreoAdmin("");
  };

  // ==========================================================
  // ABRIR MODAL
  // ==========================================================

  const abrirModalAdmin = () => {
    if (!empresaSeleccionada) {
      setMensaje({
        tipo: "error",
        texto:
          "Debe seleccionar una empresa antes de crear el administrador.",
      });

      return;
    }

    if (adminHomeCare) {
      setMensaje({
        tipo: "warning",
        texto:
          "El tenant seleccionado ya posee un administrador HomeCare.",
      });

      return;
    }

    limpiarFormularioAdmin();

    setMensaje(null);
    setModalAdminOpen(true);
  };

  // ==========================================================
  // CERRAR MODAL
  // ==========================================================

  const cerrarModalAdmin = () => {
    if (creandoAdmin) return;

    setModalAdminOpen(false);

    limpiarFormularioAdmin();
  };

  // ==========================================================
  // VALIDAR FORMULARIO
  // ==========================================================

  const validarFormularioAdmin = () => {
    const nombre =
      normalizarTexto(nombreAdmin);

    const apellido =
      normalizarTexto(apellidoAdmin);

    const correo =
      normalizarCorreo(correoAdmin);

    if (!nombre) {
      return {
        ok: false,
        mensaje:
          "Debe ingresar el nombre del administrador.",
      };
    }

    if (!apellido) {
      return {
        ok: false,
        mensaje:
          "Debe ingresar el apellido del administrador.",
      };
    }

    if (!correo) {
      return {
        ok: false,
        mensaje:
          "Debe ingresar el correo del administrador.",
      };
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(correo)) {
      return {
        ok: false,
        mensaje:
          "El correo ingresado no tiene un formato válido.",
      };
    }

    return {
      ok: true,

      data: {
        nombre,
        apellido,
        correo,
      },
    };
  };

  // ==========================================================
  // OBTENER USUARIO BACKOFFICE ACTUAL
  // ==========================================================

  const obtenerAdministradorActual = async () => {
    const usuarioAuth =
      auth.currentUser;

    if (!usuarioAuth?.uid) {
      throw new Error(
        "No existe una sesión válida de BackOffice."
      );
    }

    const snapUsuario =
      await getDoc(
        doc(
          db,
          "usuarios",
          usuarioAuth.uid
        )
      );

    if (!snapUsuario.exists()) {
      throw new Error(
        "No se encontró el perfil del usuario BackOffice."
      );
    }

    const usuario = {
      id: snapUsuario.id,
      ...snapUsuario.data(),
    };

    const rol =
      String(
        usuario.rol || ""
      )
        .trim()
        .toUpperCase();

    if (
      rol !== "DADMIN" &&
      rol !== "JADMIN"
    ) {
      throw new Error(
        "Solo DADMIN o JADMIN pueden crear el administrador inicial HomeCare."
      );
    }

    if (usuario.activo === false) {
      throw new Error(
        "El usuario BackOffice se encuentra bloqueado."
      );
    }

    return {
      ...usuario,
      rol,
    };
  };

  // ==========================================================
  // VERIFICAR ADMIN-HC EXISTENTE
  // ==========================================================

  const verificarAdminExistente = async () => {
    const qAdmin = query(
      collection(db, "usuarios"),
      where(
        "emp_id",
        "==",
        empresaId
      ),
      where(
        "rol",
        "==",
        "ADMIN-HC"
      ),
      limit(1)
    );

    const snap =
      await getDocs(qAdmin);

    return !snap.empty;
  };

  // ==========================================================
  // VERIFICAR CORREO GLOBAL
  // ==========================================================

  const verificarCorreoExistente = async (
    correo
  ) => {
    const qCorreo = query(
      collection(db, "usuarios"),
      where(
        "correo",
        "==",
        correo
      ),
      limit(1)
    );

    const snap =
      await getDocs(qCorreo);

    return !snap.empty;
  };

  // ==========================================================
  // VERIFICAR CAPACIDAD DEL PLAN
  // ==========================================================

  const verificarCapacidadTenant =
    async () => {
      const maxUsuarios =
        obtenerMaxUsuarios(
          empresaSeleccionada
        );

      const qUsuarios = query(
        collection(db, "usuarios"),
        where(
          "emp_id",
          "==",
          empresaId
        )
      );

      const snap =
        await getDocs(qUsuarios);

      const activos =
        snap.docs.filter(
          (item) =>
            item.data()?.activo !== false
        ).length;

      return {
        permitido:
          activos < maxUsuarios,

        activos,
        maxUsuarios,
      };
    };

  // ==========================================================
  // MENSAJE ERROR FIREBASE
  // ==========================================================

  const obtenerMensajeError = (error) => {
    const code =
      String(
        error?.code || ""
      );

    if (
      code.includes(
        "email-already-in-use"
      )
    ) {
      return (
        "El correo ya está registrado en Firebase Authentication."
      );
    }

    if (
      code.includes(
        "invalid-email"
      )
    ) {
      return (
        "El correo ingresado no es válido."
      );
    }

    if (
      code.includes(
        "weak-password"
      )
    ) {
      return (
        "Firebase rechazó la contraseña temporal generada."
      );
    }

    if (
      code.includes(
        "permission-denied"
      )
    ) {
      return (
        "Firestore rechazó la operación por permisos insuficientes."
      );
    }

    if (
      code.includes(
        "network-request-failed"
      )
    ) {
      return (
        "No fue posible conectar con Firebase. Revise la conexión a Internet."
      );
    }

    return (
      error?.message ||
      "No fue posible crear el administrador HomeCare."
    );
  };

  // ==========================================================
  // CREAR ADMIN HOMECARE
  // ==========================================================

  const crearAdministradorHomeCare =
    async () => {
      if (creandoAdmin) return;

      // --------------------------------------------------------
      // EMPRESA
      // --------------------------------------------------------

      if (
        !empresaSeleccionada ||
        !empresaId
      ) {
        await Swal.fire({
          icon: "warning",
          title:
            "Tenant no seleccionado",
          text:
            "Debe seleccionar una empresa antes de crear el administrador.",
          confirmButtonColor:
            "#0d9da5",
        });

        return;
      }

      // --------------------------------------------------------
      // VALIDAR FORMULARIO
      // --------------------------------------------------------

      const validacion =
        validarFormularioAdmin();

      if (!validacion.ok) {
        await Swal.fire({
          icon: "warning",
          title:
            "Datos incompletos",
          text:
            validacion.mensaje,
          confirmButtonColor:
            "#0d9da5",
        });

        return;
      }

      let usuarioAuthCreado = null;

      let firestoreCreado = false;

      let correoResetEnviado = false;

      let errorCorreoReset = null;

      try {
        setCreandoAdmin(true);

        // ======================================================
        // 1. VALIDAR USUARIO BACKOFFICE
        // ======================================================

        const adminActual =
          await obtenerAdministradorActual();

        // ======================================================
        // 2. VERIFICACIÓN FRESCA DE ADMIN-HC
        // ======================================================
        //
        // No confiamos solamente en el estado React.
        //
        // Volvemos a consultar Firestore justo antes de crear.
        // ======================================================

        const existeAdmin =
          await verificarAdminExistente();

        if (existeAdmin) {
          throw new Error(
            "El tenant seleccionado ya posee un administrador HomeCare."
          );
        }

        // ======================================================
        // 3. VERIFICAR CORREO EN FIRESTORE
        // ======================================================

        const correoYaExiste =
          await verificarCorreoExistente(
            validacion.data.correo
          );

        if (correoYaExiste) {
          throw new Error(
            "Ya existe un usuario registrado con ese correo."
          );
        }

        // ======================================================
        // 4. VERIFICAR CAPACIDAD DEL PLAN
        // ======================================================

        const capacidad =
          await verificarCapacidadTenant();

        if (!capacidad.permitido) {
          throw new Error(
            `El tenant alcanzó el máximo de usuarios permitidos (${capacidad.activos}/${capacidad.maxUsuarios}).`
          );
        }

        // ======================================================
        // 5. GENERAR PASSWORD TEMPORAL
        // ======================================================

        const passwordTemporal =
          generarPasswordTemporal();

        // ======================================================
        // 6. CREAR USUARIO FIREBASE AUTH
        // ======================================================
        //
        // Se usa secondaryAuth.
        //
        // La sesión principal de DADMIN/JADMIN NO cambia.
        // ======================================================

        const credential =
          await createUserWithEmailAndPassword(
            secondaryAuth,
            validacion.data.correo,
            passwordTemporal
          );

        usuarioAuthCreado =
          credential.user;

        const nuevoUid =
          credential.user.uid;

        // ======================================================
        // 7. PREPARAR DATOS USUARIO
        // ======================================================

        const nombreEmpresa =
          empresaSeleccionada.empresa ||
          empresaSeleccionada.nombre ||
          "";

        const correoEjecutor =
          adminActual.correo ||
          auth.currentUser?.email ||
          "";

        const nuevoUsuario = {
          nombre:
            validacion.data.nombre,

          apellido:
            validacion.data.apellido,

          correo:
            validacion.data.correo,

          rol: "ADMIN-HC",

          activo: true,

          emp_id:
            empresaId,

          empresa:
            nombreEmpresa,

          origen:
            "BACKOFFICE",

          es_admin_inicial:
            true,

          user_add:
            correoEjecutor,

          user_mod:
            correoEjecutor,

          fecha_add:
            serverTimestamp(),

          fecha_mod:
            serverTimestamp(),
        };

        // ======================================================
        // 8. AUDITORÍA
        // ======================================================

        const auditoriaRef =
          doc(
            collection(
              db,
              "auditoria_usuarios"
            )
          );

        const auditoria = {
          accion:
            "CREAR_ADMIN_HOMECARE",

          modulo:
            "BACKOFFICE",

          dominio:
            "HOMECARE",

          entidad:
            "USUARIO",

          emp_id:
            empresaId,

          empresa:
            nombreEmpresa,

          objetivo_id:
            nuevoUid,

          objetivo_descripcion:
            validacion.data.correo,

          rol_objetivo:
            "ADMIN-HC",

          ejecutado_por_uid:
            auth.currentUser.uid,

          ejecutado_por_correo:
            correoEjecutor,

          ejecutado_por_rol:
            adminActual.rol,

          datos_anteriores:
            null,

          datos_nuevos: {
            nombre:
              validacion.data.nombre,

            apellido:
              validacion.data.apellido,

            correo:
              validacion.data.correo,

            rol:
              "ADMIN-HC",

            activo:
              true,
          },

          resultado:
            "OK",

          fecha:
            serverTimestamp(),
        };

        // ======================================================
        // 9. BATCH FIRESTORE
        // ======================================================
        //
        // usuarios/{uid}
        // auditoria_usuarios/{id}
        //
        // Se escriben juntos.
        // ======================================================

        const batch =
          writeBatch(db);

        batch.set(
          doc(
            db,
            "usuarios",
            nuevoUid
          ),
          nuevoUsuario
        );

        batch.set(
          auditoriaRef,
          auditoria
        );

        await batch.commit();

        firestoreCreado = true;

        // ======================================================
        // 10. ENVIAR CORREO RESET PASSWORD
        // ======================================================
        //
        // Si el correo falla NO eliminamos la cuenta.
        //
        // Authentication + Firestore ya quedaron creados.
        // ======================================================

        try {
          await sendPasswordResetEmail(
            secondaryAuth,
            validacion.data.correo
          );

          correoResetEnviado = true;
        } catch (errorReset) {
          console.error(
            "ADMIN-HC creado, pero no se pudo enviar correo de contraseña:",
            errorReset
          );

          errorCorreoReset =
            errorReset;
        }

        // ======================================================
        // 11. CERRAR MODAL
        // ======================================================

        setModalAdminOpen(false);

        limpiarFormularioAdmin();

        // ======================================================
        // 12. RESULTADO
        // ======================================================

        if (correoResetEnviado) {
          await Swal.fire({
            icon: "success",
            title:
              "Administrador creado",
            text:
              `Se creó correctamente ${validacion.data.nombre} ${validacion.data.apellido} (${validacion.data.correo}). Se envió un correo para que defina su contraseña.`,
            confirmButtonText:
              "Aceptar",
            confirmButtonColor:
              "#0d9da5",
          });
        } else {
          await Swal.fire({
            icon: "warning",
            title:
              "Administrador creado",
            text:
              `El usuario ${validacion.data.correo} fue creado correctamente, pero no fue posible enviar el correo para definir su contraseña. La cuenta NO fue eliminada.`,
            confirmButtonText:
              "Aceptar",
            confirmButtonColor:
              "#f2711c",
          });

          console.error(
            "Detalle error correo reset:",
            errorCorreoReset
          );
        }

        /*
         * No es necesario volver a consultar usuarios.
         *
         * El onSnapshot detectará automáticamente
         * usuarios/{nuevoUid}.
         */
      } catch (error) {
        console.error(
          "Error creando ADMIN-HC:",
          error
        );

        // ======================================================
        // ROLLBACK FIREBASE AUTH
        // ======================================================
        //
        // Si Authentication creó la cuenta PERO Firestore no
        // consiguió guardar usuarios/{uid}, eliminamos la cuenta
        // creada para evitar un usuario huérfano.
        //
        // Si Firestore YA confirmó el batch, NO hacemos rollback.
        // ======================================================

        if (
          usuarioAuthCreado &&
          !firestoreCreado
        ) {
          try {
            await deleteUser(
              usuarioAuthCreado
            );

            console.log(
              "Rollback Auth realizado correctamente."
            );
          } catch (rollbackError) {
            console.error(
              "No fue posible eliminar el usuario Auth durante rollback:",
              rollbackError
            );
          }
        }

        const mensajeError =
          obtenerMensajeError(error);

        await Swal.fire({
          icon: "error",
          title:
            "No fue posible crear el administrador",
          text:
            mensajeError,
          confirmButtonColor:
            "#db2828",
        });
      } finally {
        // ======================================================
        // CERRAR SESIÓN SECONDARY AUTH
        // ======================================================

        try {
          if (
            secondaryAuth.currentUser
          ) {
            await signOut(
              secondaryAuth
            );
          }
        } catch (errorSignOut) {
          /*
           * El fallo de signOut NO invalida la creación.
           *
           * No hacemos rollback por este error.
           */
          console.error(
            "No fue posible cerrar secondaryAuth:",
            errorSignOut
          );
        }

        setCreandoAdmin(false);
      }
    };

  // ==========================================================
  // LOADING EMPRESAS
  // ==========================================================

  if (loadingEmpresas) {
    return (
      <Container
        fluid
        style={{
          padding: 24,
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <Loader
          active
          inline="centered"
        >
          Cargando empresas...
        </Loader>
      </Container>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1400,
        margin: "0 auto",
        padding: "24px 28px",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
      {/* ==================================================== */}
      {/* PAGE HEADER */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: 12,
            background: "#e5f7f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            name="server"
            style={{
              margin: 0,
              color: "#0d9da5",
              fontSize: 21,
            }}
          />
        </div>

        <div>
          <Header
            as="h2"
            style={{
              margin: 0,
              color: "#172b4d",
              fontWeight: 700,
            }}
          >
            Estado Tenant
          </Header>

          <div
            style={{
              color: "#8793a5",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            Revisión operacional, comercial y administrativa de empresas clientes
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/* MENSAJES */}
      {/* ==================================================== */}

      {mensaje && (
        <Message
          positive={
            mensaje.tipo === "success"
          }
          negative={
            mensaje.tipo === "error"
          }
          warning={
            mensaje.tipo === "warning"
          }
          onDismiss={() =>
            setMensaje(null)
          }
          style={{
            boxShadow: "none",
            borderRadius: 7,
          }}
        >
          <Icon
            name={
              mensaje.tipo ===
                "success"
                ? "check circle"
                : "warning sign"
            }
          />

          {mensaje.texto}
        </Message>
      )}

      {/* ==================================================== */}
      {/* SELECTOR TENANT */}
      {/* ==================================================== */}

      <Segment
        style={{
          padding: 0,
          borderRadius: 8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          overflow: "visible",
        }}
      >
        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#253858",
            fontWeight: 600,
          }}
        >
          <Icon
            name="building outline"
            style={{
              color: "#2185d0",
            }}
          />

          Empresa / Tenant
        </div>

        <div
          style={{
            padding: 18,
          }}
        >
          {empresas.length === 0 ? (
            <Message warning>
              <Icon name="warning sign" />

              No existen empresas registradas.
            </Message>
          ) : (
            <Dropdown
              fluid
              search
              selection
              placeholder="Seleccione empresa"
              options={
                opcionesEmpresas
              }
              value={empresaId}
              onChange={(
                e,
                { value }
              ) =>
                cambiarEmpresa(
                  value
                )
              }
            />
          )}
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* TENANT */}
      {/* ==================================================== */}

      {empresaSeleccionada && (
        <>
          {loadingUsuarios ? (
            <Segment
              style={{
                marginTop: 18,
                minHeight: 180,
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                border:
                  "1px solid #e5e9ef",
                boxShadow: "none",
                borderRadius: 8,
              }}
            >
              <Loader
                active
                inline="centered"
              >
                Cargando información del tenant...
              </Loader>
            </Segment>
          ) : (
            <EstadoTenant
              empresa={
                empresaSeleccionada
              }
              usuariosActivos={
                usuariosActivos
              }
              adminHomeCare={
                adminHomeCare
              }
              loadingAdmin={
                loadingUsuarios
              }
              onCrearAdministrador={
                abrirModalAdmin
              }
            />
          )}
        </>
      )}

      {/* ==================================================== */}
      {/* ADVERTENCIA INTEGRIDAD */}
      {/* ==================================================== */}

      {!loadingUsuarios &&
        adminsHomeCare.length >
        1 && (
          <Message
            warning
            style={{
              marginTop: 14,
              boxShadow: "none",
            }}
          >
            <Icon name="warning sign" />

            Se detectaron{" "}
            <strong>
              {
                adminsHomeCare.length
              }
            </strong>{" "}
            usuarios con rol ADMIN-HC
            para este tenant. Conviene
            revisar esta configuración.
          </Message>
        )}

      {/* ==================================================== */}
      {/* MODAL CREAR ADMIN */}
      {/* ==================================================== */}

      <Modal
        size="small"
        open={modalAdminOpen}
        onClose={
          cerrarModalAdmin
        }
        closeOnEscape={
          !creandoAdmin
        }
        closeOnDimmerClick={
          !creandoAdmin
        }
        style={{
          borderRadius: 9,
          overflow: "hidden",
        }}
      >
        <Modal.Header
          style={{
            padding:
              "16px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                minWidth: 42,
                borderRadius: 10,
                background:
                  "#e5f7f7",
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
              }}
            >
              <Icon
                name="user plus"
                style={{
                  margin: 0,
                  color:
                    "#0d9da5",
                  fontSize: 18,
                }}
              />
            </div>

            <div>
              <div
                style={{
                  color:
                    "#172b4d",
                  fontWeight: 700,
                  fontSize: 17,
                }}
              >
                Crear Administrador HomeCare
              </div>

              <div
                style={{
                  color:
                    "#8793a5",
                  fontSize: 12,
                  fontWeight: 400,
                  marginTop: 2,
                }}
              >
                Provisionamiento inicial del tenant
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Content>
          {/* EMPRESA */}

          <div
            style={{
              padding: 13,
              marginBottom: 18,
              borderRadius: 7,
              border:
                "1px solid #e5e9ef",
              background:
                "#f8fafc",
            }}
          >
            <div
              style={{
                color:
                  "#8793a5",
                fontSize: 11,
              }}
            >
              Tenant
            </div>

            <div
              style={{
                marginTop: 4,
                color:
                  "#344563",
                fontWeight: 700,
              }}
            >
              <Icon
                name="building outline"
                style={{
                  color:
                    "#2185d0",
                }}
              />

              {empresaSeleccionada?.empresa ||
                empresaSeleccionada?.nombre ||
                "-"}
            </div>

            <div
              style={{
                marginTop: 9,
                display: "flex",
                gap: 7,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  padding:
                    "4px 8px",
                  borderRadius: 4,
                  background:
                    "#e9f3fb",
                  color:
                    "#2185d0",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                ADMIN-HC
              </span>

              <span
                style={{
                  padding:
                    "4px 8px",
                  borderRadius: 4,
                  background:
                    "#e5f7f7",
                  color:
                    "#0d9da5",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {empresaId}
              </span>
            </div>
          </div>

          {/* FORM */}

          <Form>
            <Form.Field required>
              <label>
                Nombre
              </label>

              <Form.Input
                fluid
                autoComplete="off"
                icon="user outline"
                iconPosition="left"
                placeholder="Nombre"
                value={
                  nombreAdmin
                }
                disabled={
                  creandoAdmin
                }
                onChange={(e) =>
                  setNombreAdmin(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            <Form.Field required>
              <label>
                Apellido
              </label>

              <Form.Input
                fluid
                autoComplete="off"
                icon="user outline"
                iconPosition="left"
                placeholder="Apellido"
                value={
                  apellidoAdmin
                }
                disabled={
                  creandoAdmin
                }
                onChange={(e) =>
                  setApellidoAdmin(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            <Form.Field required>
              <label>
                Correo
              </label>

              <Form.Input
                fluid
                type="email"
                autoComplete="off"
                icon="mail outline"
                iconPosition="left"
                placeholder="administrador@cliente.cl"
                value={
                  correoAdmin
                }
                disabled={
                  creandoAdmin
                }
                onChange={(e) =>
                  setCorreoAdmin(
                    e.target.value
                  )
                }
              />
            </Form.Field>
          </Form>

          <Message
            info
            size="small"
            style={{
              marginTop: 18,
              boxShadow: "none",
            }}
          >
            <Icon
              name="shield alternate"
              color="blue"
            />

            El rol{" "}
            <strong>
              ADMIN-HC
            </strong>{" "}
            y el tenant serán
            asignados automáticamente
            por BackOffice.
          </Message>

          <Message
            size="small"
            style={{
              boxShadow: "none",
            }}
          >
            <Icon
              name="mail outline"
              style={{
                color:
                  "#6435c9",
              }}
            />

            El usuario recibirá un
            correo para definir su
            contraseña personal. El
            operador de BackOffice no
            conocerá su contraseña
            definitiva.
          </Message>

          <Message
            size="small"
            style={{
              boxShadow: "none",
            }}
          >
            <Icon
              name="history"
              style={{
                color:
                  "#6435c9",
              }}
            />

            La creación quedará
            registrada en{" "}
            <strong>
              auditoria_usuarios
            </strong>
            , incluyendo tenant,
            usuario creado y
            operador BackOffice.
          </Message>
        </Modal.Content>

        <Modal.Actions
          style={{
            background:
              "#f8fafc",
            borderTop:
              "1px solid #e5e9ef",
          }}
        >
          <Button
            type="button"
            disabled={
              creandoAdmin
            }
            onClick={
              cerrarModalAdmin
            }
            style={{
              background:
                "#ffffff",
              color:
                "#526273",
              border:
                "1px solid #d7dee7",
              boxShadow: "none",
            }}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            loading={
              creandoAdmin
            }
            disabled={
              creandoAdmin
            }
            onClick={
              crearAdministradorHomeCare
            }
            style={{
              background:
                "#0d9da5",
              color:
                "#ffffff",
              boxShadow: "none",
            }}
          >
            {!creandoAdmin && (
              <Icon
                name="user plus"
                style={{
                  color:
                    "#ffffff",
                }}
              />
            )}

            {creandoAdmin
              ? "Creando..."
              : "Crear Administrador"}
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default EstadoTenantPage;